<script setup lang="ts">
  import {
    type CustomerCase,
    type CustomerDataMapper as DataMapper
  } from '@/types/generated/types.gen'
  import { computed, type PropType } from 'vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'

  const props = defineProps({
    caseData: {
      type: Object as PropType<CustomerCase>,
      required: true
    },
    mappers: {
      type: Array as PropType<DataMapper[]>,
      required: true
    }
  })

  const mappersWithValueCount = computed<number>(() => {
    return props.mappers?.filter(
      (mapper) => mapper.data && mapper.is_law_specific_field && mapper.law_field === 'Arbeitsrecht'
    ).length
  })
  const isOnLawSpecificDataPage = computed<boolean>(() => {
    return window.location.pathname.includes('law-specific-data')
  })
</script>

<template>
  <div
    class="flex min-h-screen flex-col"
    v-if="caseData"
  >
    <div class="legacy-bg-color flex flex-grow items-start justify-center">
      <div class="container px-16 py-8">
        <div class="legacy-case-title mb-2 flex items-center justify-between">
          Anfrage von
          {{ `${caseData?.data.client.first_name.charAt(0)}. ${caseData?.data.client.last_name}` }}
        </div>
        <nav class="flex gap-x-2">
          <a
            class="active inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap px-1 py-2 text-sm
              hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            :href="`/cases/${caseData.id}/`"
          >
            Allgemein
          </a>
          <a
            class="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap px-1 py-2 text-sm text-gray-500
              hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            :class="
              isOnLawSpecificDataPage
                ? 'border-b-2 border-purple-600 font-semibold text-purple-600'
                : ''
            "
            :href="`/app/cases/${caseData.uuid}/law-specific-data`"
          >
            Rechtsbezogene Daten
            <span
              v-if="mappers.length > 0"
              class="badge bg-purple"
            >
              {{ mappersWithValueCount }}
            </span>
            <SkeletonLoader
              v-else
              class="h-5 w-5"
            />
          </a>
          <a
            class="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap px-1 py-2 text-sm text-gray-500
              hover:text-purple-600 focus:text-purple-600 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            :class="
              isOnLawSpecificDataPage
                ? ''
                : 'border-b-2 border-purple-600 font-semibold text-purple-600'
            "
            :href="`/app/cases/${caseData.uuid}/chat`"
          >
            KI Chat
          </a>
        </nav>
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
  a:hover {
    text-decoration: none;
  }

  .legacy-bg-color {
    background-color: #f8fafc !important;
  }

  .legacy-case-title {
    font-weight: 600 !important;
    white-space: nowrap !important;
    font-size: 1.5rem !important;
    line-height: 1.3333333;
    margin-top: 0;
    margin-bottom: 0.5rem;
    margin-block-end: 0.67em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    unicode-bidi: isolate;
    text-rendering: optimizeLegibility;
    font-feature-settings: 'liga' 0;
    letter-spacing: 0;
  }
</style>
